<script setup lang="ts">
import type { IProps, IEmits } from './UiDrawer.types'

const contentMaxHeight = ref<number>(0)
const content = ref<HTMLElement | null>(null)

const { isOpen } = defineProps<IProps>()
const emit = defineEmits<IEmits>()

const onOpenDrawerClick = () => {
  emit('clicked')
}

const calculateTruncation = () => {
  if (!content.value) return
  content.value.style.maxHeight = `${isOpen ? contentMaxHeight.value + 20 : '0'}px`
}

let ro: ResizeObserver | null = null
let resizeObserverTimeout: ReturnType<typeof setTimeout> | null = null
let previousContentWidth: number | null = null

onMounted(() => {
  const contentHeight = content?.value?.clientHeight
  contentMaxHeight.value = contentHeight!

  nextTick(() => {
    calculateTruncation()
  })

  ro = new ResizeObserver((entries) => {
    clearTimeout(resizeObserverTimeout!)
    const entry = entries[0]
    const entryContentWidth = entry.contentRect.width

    if (entryContentWidth !== previousContentWidth) {
      resizeObserverTimeout = setTimeout(() => {
        const entryDescription = entry.target.querySelector('.single-drawer__description') as HTMLElement
        contentMaxHeight.value = entryDescription?.clientHeight || 0
        previousContentWidth = entryContentWidth
        calculateTruncation()
      }, 200)
    }
  })

  if (content.value) {
    ro.observe(content.value)
  }
})

onUnmounted(() => {
  if (ro && content.value) {
    ro.unobserve(content.value)
    clearTimeout(resizeObserverTimeout!)
  }
})

watch(() => isOpen, () => {
  calculateTruncation()
})

</script>

<template>
  <div
    :class="`single-drawer single-drawer--${variant}`"
    @click.stop="onOpenDrawerClick"
  >
    <div class="single-drawer__header">
      <img
        v-if="icon"
        :src="icon"
        :alt="header"
        class="single-drawer__icon"
      >

      <p :class="['single-drawer__header-text', `${isOpen ? `single-drawer__header-text--open${variant}` : ''}`]">
        {{ header }}
      </p>

      <div class="single-drawer__header-icon">
        <div class="single-drawer__header-icon-line" />

        <div
          v-if="!isOpen"
          class="single-drawer__header-icon-line single-drawer__header-icon-line--vertical"
        />
      </div>
    </div>

    <div
      ref="content"
      :class="['single-drawer__description-wrapper', isOpen ? 'single-drawer__description-wrapper--open' : '']"
    >
      <div class="single-drawer__description-line" />

      <span
        :class="`single-drawer__description single-drawer__description--${variant}`"
        v-html="description"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.single-drawer {
  width: 100%;
  margin-bottom: 0.8rem;
  padding: 2.4rem 3.6rem;
  background-color: $white;
  border-radius: 0.8rem;
  cursor: pointer;

  &--tertiary {
    padding: 2rem;
    border-radius: 2rem;

    .single-drawer__description {
      color: $text-400;
    }

    .single-drawer__header-text {
      padding-right: 2rem;
    }
  }

  &__header {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    min-height: 3.6rem;
  }

  &__header-text {
    @include h10;
    max-width: calc(100% - 3.2rem);
    padding-right: 2.4rem;
    color: $text-400;

    &--open-secondary {
      color: $purple-400;
    }
  }

  &__header-icon {
    position: relative;
    min-width: 3.2rem;
    width: 3.2rem;
    height: 3.2rem;
    margin-left: auto;
    border: 2px solid $gray-400;
    border-radius: 50%;
  }

  &__header-icon-line {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1.1rem;
    height: 0.2rem;
    background-color: $gray-400;
    transform: translate(-50%, -50%);

    &--vertical {
      width: 0.2rem;
      height: 1.1rem;
    }
  }

  &__description-wrapper {
    overflow: hidden;
    transition: 0.5s;
  }

  &__description-line {
    width: 100%;
    height: 1px;
    margin-top: 1.6rem;
    background-color: $gray-300;
  }

  &__description {
    @include body-2-medium;
    display: inline-block;
    margin: 1.6rem 0 0 0;
    padding: 0 0 1.6rem 0;
    color: $text-300;

    &--tertiary {
      :deep(ul li) {
        position: relative;
        display: inline-block;
        margin-bottom: 1.2rem;
        padding-left: 4.8rem;

        &:before {
          position: absolute;
          top: 1px;
          left: 0.4rem;
          width: 2.4rem;
          height: 2.4rem;
          background-image: url('/icons/lipoint.png');
          background-repeat: no-repeat;
          background-position: center;
          background-size: 100%;
          border-radius: 0.8rem;
          content: '';
        }
      }

      :deep(ul li:last-child) {
        margin-bottom: 0;
      }
    }
  }

  &__icon {
    max-width: 7.2rem;
    max-height: 7.2rem;
    margin-right: 1.6rem;
  }
}

@include atM {
  .single-drawer {

    &--tertiary {
      padding: 2.4rem 3.6rem;

      .single-drawer__header-text {
        padding-right: 2.4rem;
      }
    }

    &__header-text {
      @include h9;

      &--open-primary {
        @include h7;
      }

      &--open-secondary {
        @include h9;
        color: $purple-400;
      }
    }

    &__description {
      padding: 0 4.1rem 1.6rem 0;
    }
  }
}
</style>
